import React, { useEffect, useState } from 'react';
import { useTrackingContext } from '../../../context/trackingContext';
import TwitchLogo from '../../../assets/images/Twitch.svg';
import DiscordLogo from '../../../assets/images/discord.svg';
import PSLogo from '../../../assets/images/playstation.svg';
import BarbLogo from '../../../assets/images/barblogo.png';
import useClient from '../../../hooks/useClient';
import config from '../../../config';
import { useUserContext } from '../../../context/userContext';
import { useLocation, useSearchParams } from 'react-router-dom';
import Alert from '../../../components/Alert';
import Dialog from '../../../components/Dialog';
import rocketBoyImg from '../../../assets/images/barbfullLogo.png';
import { Controller, useForm } from 'react-hook-form';
import Input from '../../../components/Input';
import ButtonAuth from '../../../components/ButtonAuth';

const LinkAccounts = () => {
  const { trackPageView, trackFormSubmit, trackError } = useTrackingContext();
  const { userData, fetchUser } = useUserContext();
  const [_params, setParams] = useSearchParams();

  const location = useLocation();

  const client = useClient();

  const { twitchName, discordName } = userData || {};

  const [internalAlert, setInternalAlert] = useState(null);
  const [linking, setLinking] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [dialogAlert, setDialogAlert] = useState(false);
  const [devName, setDevName] = useState(null);
  const [stageName, setStageName] = useState(null);
  const [ugcName, setUgcName] = useState(null);
  const [demoName, setDemoName] = useState(null);
  const [paLoading, setPaLoading] = useState(true);
  const [platformAccounts, setPlatformAccounts] = useState([]);

  useEffect(() => {
    trackPageView({
      pageName: 'Account - LinkAccounts'
    });
  }, []);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    reset
  } = useForm({ mode: 'onChange' });

  const onSubmit = async (data) => {
    const { email, password } = data;

    trackFormSubmit({
      formName: 'Login linked form submit'
    });

    try {
      await client.post(`${config.everywhereApi}/users/link-account`, {
        accountName: email,
        password,
        env: linking
      });

      setLinking(null);
      reset();
    } catch (err) {
      const error = err?.response?.data?.error;

      trackError({ error_text: error?.errorMessage });

      setDialogAlert({
        text: error ? error?.errorMessage + ' - ' + error.errorCode : 'Unknown error',
        success: false
      });
    } finally {
      await lookupExistingInternalAccounts();
    }
  };

  const link = async (platform) => {
    try {
      if (platform === 'Twitch') {
        const { data } = await client.get(
          `${config.everywhereApi}/users/self/profile/link-twitch-account-code`
        );

        const a = document.createElement('a');
        a.href = data.data.link;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }

      if (platform === 'Discord') {
        const { data } = await client.get(
          `${config.everywhereApi}/users/self/profile/link-discord-account-code`
        );

        const a = document.createElement('a');
        a.href = data.data.link;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }

      if (platform === 'PSN') {
        window.open(`${config.everywhereApi}/users/self/profile/psn-linking-link`, '_self');
        return;
      }
    } catch (err) {
      console.error('Unable to link platform', err);
    }
  };

  const unlinkInternal = async (env) => {
    try {
      await client.post(`${config.everywhereApi}/users/unlink-account`, {
        env
      });
    } catch (err) {
      console.error('Unable to unlink internal platform', err);
    } finally {
      await lookupExistingInternalAccounts();
    }
  };

  const unlink = async (platform) => {
    try {
      if (platform === 'Twitch') {
        await client.post(`${config.everywhereApi}/users/self/profile/unlink-twitch-account`);
      } else if (platform === 'Discord') {
        await client.post(`${config.everywhereApi}/users/self/profile/unlink-discord-account`);
      } else if (platform === 'PSN') {
        window.open(`${config.everywhereApi}/users/self/profile/psn-unlinking-link`, '_self');
        return;
      }
    } catch {
      console.error('Unable to unlink account');
    } finally {
      fetchUser();
    }
  };

  useEffect(() => {
    fetchUser();

    // Set linked platform accounts
    client
      .get(`${config.everywhereApi}/users/self/profile/linked-platform-accounts`)
      .then(({ data }) => setPlatformAccounts(data.data))
      .finally(() => setPaLoading(false));

    const query = new URLSearchParams(location.search);

    const tab = query.get('tab');
    const fail = query.get('fail');
    const unlink = query.get('unlink');

    if (tab && !fail) {
      setInternalAlert({
        text: `${tab} account ${unlink ? 'unlinked' : 'linked'} successfully.`,
        success: true
      });
    }

    if (fail === 'discorddupe') {
      setInternalAlert({
        text: 'Your Discord account is already linked to a Build A Rocket Boy Account.',
        success: false
      });
    }

    if (fail === 'twitchdupe') {
      setInternalAlert({
        text: 'Your Twitch account is already linked to a Build A Rocket Boy Account.',
        success: false
      });
    }

    if (tab === 'PSN' && fail === 'true') {
      setInternalAlert({
        text: `Your PSN account couldn't be ${unlink ? 'unlinked' : 'linked'}.`,
        success: false
      });
    }

    if (fail === 'unknown') {
      setInternalAlert({
        text: 'There was a problem linking the account.',
        success: false
      });
    }
    // Clear consumed query strings
    setParams({});
  }, []);

  const lookupExistingInternalAccounts = async () => {
    const { data } = await client.get(`${config.everywhereApi}/users/self/profile/linked-accounts`);

    const { devUsername, stageUsername, ugcUsername, demoUsername } = data.linkedAccounts;

    setDevName(devUsername);
    setStageName(stageUsername);
    setUgcName(ugcUsername);
    setDemoName(demoUsername);
  };

  useEffect(() => {
    if (userData?.accountsLinkable && process.env.REACT_APP_STAGE === 'production')
      lookupExistingInternalAccounts();
  }, [userData?.accountsLinkable]);

  const psnAccount = platformAccounts.find((pa) => pa.platformName === 'psn');

  return (
    <div id="LinkAccounts">
      <div className="mb-10 mt-5">
        {userData?.externalAccountsLinkable && (
          <div className="mb-16">
            <p className="mb-5">
              Select the account below you would like to link. Additional linking options will be
              added later.
            </p>

            <div className="flex flex-row gap-6 flex-wrap">
              {/* Twitch */}
              <div className="flex flex-col gap-4 w-full sm:w-[150px]">
                <button
                  onClick={() => link('Twitch')}
                  disabled={twitchName}
                  className={`border border-gray flex flex-col gap-4 justify-center w-full h-[250px] sm:h-[150px] sm:w-[150px] ${
                    !twitchName && 'hover:shadow-[1px_1px_gray] transition-all duration-75'
                  }`}>
                  <div className="sm:h-[80px] w-full flex flex-col justify-center">
                    <img className="mx-auto w-1/3" src={TwitchLogo} alt="twitch-logo" />
                  </div>
                  <p className="mx-auto font-Korolev-Medium text-lg truncate">
                    {twitchName ? twitchName : 'Twitch'}
                  </p>
                </button>
                <div className="mx-auto">
                  {twitchName ? (
                    <button className="underline" onClick={() => unlink('Twitch')}>
                      Unlink Account
                    </button>
                  ) : (
                    <button className="hover:underline" onClick={() => link('Twitch')}>
                      Link Account
                    </button>
                  )}
                </div>
              </div>

              {/* Discord */}
              <div className="flex flex-col gap-4 w-full sm:w-[150px]">
                <button
                  onClick={() => link('Discord')}
                  disabled={discordName}
                  className={`border border-gray flex flex-col gap-4 justify-center w-full h-[250px] sm:h-[150px] sm:w-[150px] ${
                    !discordName && 'hover:shadow-[1px_1px_gray] transition-all duration-75'
                  }`}>
                  {' '}
                  <div className="sm:h-[80px] w-full flex flex-col justify-center">
                    <img className="mx-auto w-1/3" src={DiscordLogo} alt="discord-logo" />
                  </div>
                  <p className="mx-auto w-full font-Korolev-Medium text-lg px-3 text-ellipsis overflow-hidden">
                    {discordName ? discordName : 'Discord'}
                  </p>
                </button>
                <div className="mx-auto">
                  {discordName ? (
                    <button className="underline" onClick={() => unlink('Discord')}>
                      Unlink Account
                    </button>
                  ) : (
                    <button className="hover:underline" onClick={() => link('Discord')}>
                      Link Account
                    </button>
                  )}
                </div>
              </div>

              {/* PSN */}
              {process.env.REACT_APP_FP_LINKING === 'true' && !paLoading && (
                <div className="flex flex-col gap-4 w-full sm:w-[150px]">
                  <button
                    onClick={() => link('PSN')}
                    disabled={psnAccount}
                    className={`border border-gray flex flex-col gap-4 justify-center w-full h-[250px] sm:h-[150px] sm:w-[150px] ${
                      !psnAccount && 'hover:shadow-[1px_1px_gray] transition-all duration-75'
                    }`}>
                    {' '}
                    <div className="sm:h-[80px] w-full flex flex-col justify-center">
                      <img className="mx-auto w-1/3" src={PSLogo} alt="psn-logo" />
                    </div>
                    <p className="mx-auto w-full font-Korolev-Medium text-lg px-3 text-ellipsis overflow-hidden">
                      {psnAccount ? psnAccount.platformUsername : 'PSN'}
                    </p>
                  </button>
                  <div className="mx-auto">
                    {psnAccount ? (
                      <button className="underline" onClick={() => unlink('PSN')}>
                        Unlink Account
                      </button>
                    ) : (
                      <button className="hover:underline" onClick={() => link('PSN')}>
                        Link Account
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {internalAlert && (
              <div className="mt-10">
                <Alert setShowAlert={setInternalAlert} loggedIn success={internalAlert.success}>
                  <div className="pr-12 text-sm md:text-base">
                    <p className="px-2">{internalAlert.text}</p>
                  </div>
                </Alert>
              </div>
            )}
          </div>
        )}

        {userData?.accountsLinkable && process.env.REACT_APP_STAGE === 'production' && (
          <div className="mb-10">
            <h1 className="font-Korolev-Bold text-xl uppercase mb-2">Link Internal Accounts</h1>

            <p className="mb-5">
              Select and log in with your Build A Rocket Boy Environment Accounts to link them to
              your live account. Doing so will enable you to transfer your user-generated content
              (UGC) from one environment to another while maintaining ownership of the content.
            </p>

            <div className="flex flex-row gap-6 flex-wrap">
              {/* Stage */}
              <div className="flex flex-col gap-4 w-full sm:w-[150px]">
                <button
                  onClick={() => setLinking('staging')}
                  disabled={stageName}
                  className={`border border-gray flex flex-col gap-2 justify justify-start w-full h-[250px] sm:h-[150px] sm:w-[150px] ${
                    !stageName && 'hover:shadow-[1px_1px_gray] transition-all duration-75'
                  }`}>
                  <div className="sm:h-[80px] w-full flex flex-col justify-center">
                    <img
                      className="mx-auto max-h-[120px] sm:w-1/3"
                      src={BarbLogo}
                      alt="barb-logo"
                    />
                  </div>
                  <div className="flex-col mx-auto font-Korolev-Medium text-lg truncate flex ">
                    <p>{stageName}</p>
                    <p>STAGE</p>
                  </div>
                </button>
                <div className="mx-auto">
                  {stageName ? (
                    <button className="underline" onClick={() => unlinkInternal('staging')}>
                      Unlink Account
                    </button>
                  ) : (
                    <button className="hover:underline" onClick={() => setLinking('staging')}>
                      Link Account
                    </button>
                  )}
                </div>
              </div>

              {/* Dev */}
              <div className="flex flex-col gap-4 w-full sm:w-[150px]">
                <button
                  onClick={() => setLinking('development')}
                  disabled={devName}
                  className={`border border-gray flex flex-col gap-2 justify justify-start w-full h-[250px] sm:h-[150px] sm:w-[150px] ${
                    !devName && 'hover:shadow-[1px_1px_gray] transition-all duration-75'
                  }`}>
                  <div className="sm:h-[80px] w-full flex flex-col justify-center">
                    <img
                      className="mx-auto max-h-[120px] sm:w-1/3"
                      src={BarbLogo}
                      alt="barb-logo"
                    />
                  </div>
                  <div className="flex-col mx-auto font-Korolev-Medium text-lg truncate flex ">
                    <p>{devName}</p>
                    <p>DEV</p>
                  </div>
                </button>
                <div className="mx-auto">
                  {devName ? (
                    <button className="underline" onClick={() => unlinkInternal('development')}>
                      Unlink Account
                    </button>
                  ) : (
                    <button className="hover:underline" onClick={() => setLinking('development')}>
                      Link Account
                    </button>
                  )}
                </div>
              </div>

              {/* UGC */}
              <div className="flex flex-col gap-4 w-full sm:w-[150px]">
                <button
                  onClick={() => setLinking('ugc')}
                  disabled={ugcName}
                  className={`border border-gray flex flex-col gap-2 justify justify-start w-full h-[250px] sm:h-[150px] sm:w-[150px] ${
                    !ugcName && 'hover:shadow-[1px_1px_gray] transition-all duration-75'
                  }`}>
                  <div className="sm:h-[80px] w-full flex flex-col justify-center">
                    <img
                      className="mx-auto max-h-[120px] sm:w-1/3"
                      src={BarbLogo}
                      alt="barb-logo"
                    />
                  </div>
                  <div className="flex-col mx-auto font-Korolev-Medium text-lg truncate flex ">
                    <p>{ugcName}</p>
                    <p>UGC</p>
                  </div>
                </button>
                <div className="mx-auto">
                  {ugcName ? (
                    <button className="underline" onClick={() => unlinkInternal('ugc')}>
                      Unlink Account
                    </button>
                  ) : (
                    <button className="hover:underline" onClick={() => setLinking('ugc')}>
                      Link Account
                    </button>
                  )}
                </div>
              </div>

              {/* Demo */}
              <div className="flex flex-col gap-4 w-full sm:w-[150px]">
                <button
                  onClick={() => setLinking('demo')}
                  disabled={demoName}
                  className={`border border-gray flex flex-col gap-2 justify justify-start w-full h-[250px] sm:h-[150px] sm:w-[150px] ${
                    !demoName && 'hover:shadow-[1px_1px_gray] transition-all duration-75'
                  }`}>
                  <div className="sm:h-[80px] w-full flex flex-col justify-center">
                    <img
                      className="mx-auto max-h-[120px] sm:w-1/3"
                      src={BarbLogo}
                      alt="barb-logo"
                    />
                  </div>
                  <div className="flex-col mx-auto font-Korolev-Medium text-lg truncate flex ">
                    <p>{demoName}</p>
                    <p>DEMO</p>
                  </div>
                </button>
                <div className="mx-auto">
                  {demoName ? (
                    <button className="underline" onClick={() => unlinkInternal('demo')}>
                      Unlink Account
                    </button>
                  ) : (
                    <button className="hover:underline" onClick={() => setLinking('demo')}>
                      Link Account
                    </button>
                  )}
                </div>
              </div>
            </div>

            {internalAlert && (
              <div className="mt-10">
                <Alert setShowAlert={setInternalAlert} loggedIn success={internalAlert.success}>
                  <div className="pr-12 text-sm md:text-base">
                    <p className="px-2">{internalAlert.text}</p>
                  </div>
                </Alert>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog
        isOpen={linking}
        onClose={() => {
          setLinking(null);
          setDialogAlert(null);
        }}
        alert={dialogAlert}
        setAlert={setDialogAlert}
        body={
          <div>
            <h1 className="font-Korolev-Bold text-5xl xl:text-5xl uppercase mb-6">
              Link internal
              <br /> account
            </h1>

            <img src={rocketBoyImg} alt="build rocket boy" className="w-full mb-8" />

            <p className="text-black font-Korolev-Medium text-base mb-2">
              Please log in with your {linking} Build A Rocket Boy account to link it to your live
              account.
            </p>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-8 gap-3"></div>

              <div className="mb-5">
                <Controller
                  control={control}
                  name="email"
                  defaultValue=""
                  rules={{
                    required: true
                  }}
                  render={({ field: { value, onChange } }) => (
                    <Input
                      type="text"
                      placeholder="EMAIL ADDRESS"
                      value={value}
                      autoFocus
                      onChange={onChange}
                      className="w-full"
                      questionIcon={false}
                    />
                  )}
                />
              </div>

              <div className="mb-8">
                <Controller
                  control={control}
                  name="password"
                  defaultValue={''}
                  rules={{
                    required: true
                  }}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <Input
                      type="password"
                      placeholder="PASSWORD"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      className="w-full tracking-widest"
                      questionIcon={false}
                      passwordVisiblity
                    />
                  )}
                />
              </div>

              <ButtonAuth
                label={'LOG IN'}
                className="mb-12"
                type={'submit'}
                isSubmitting={isSubmitting}
                isDisabled={!isValid}
              />
            </form>
            {showAlert && (
              <Alert setShowAlert={setShowAlert} success={false}>
                <p className="pr-12 text-sm md:text-base">Login failed: {showAlert.text}</p>
              </Alert>
            )}
          </div>
        }
      />
    </div>
  );
};

export default LinkAccounts;
